/* eslint-disable no-await-in-loop */
/**
 *
 * useAddFiles
 *
 */

import { useRef, useReducer, useState, useMemo } from 'react';
import produce from 'immer';
import { uniqueId } from 'lodash-es';
import { getTypeCounts, typeTest } from 'utils/files.js';
import { createImageFile } from 'utils/imageFile.js';
import useUpload from 'containers/FileUpload/useUpload.js';
import { idValues, makeIdObject } from 'utils/arrays.js';
import sleep from 'utils/sleep.js';
import convertHeic from 'utils/convertHeic.js';
import { useIsMounted } from 'utils/useSafeState.js';

export const MAX_PREVIEWS = 10; // max. number of images loaded for preview
const MIN_DELAY = 100; // min delay between uploading images

const ACTION_ADD_VALUE = Symbol('Add value');
const ACTION_SET_TOTAL = Symbol('Set total number');

function useAddFiles(files, onChange) {
  const counts = useMemo(() => getTypeCounts(files.map((f) => f.file || f)), [files]);
  const upload = useUpload();
  const [skipPreviews, setSkipPreviews] = useState(false);
  const isMounted = useIsMounted();

  const reducer = produce((draft, action) => {
    if (!isMounted) {
      return;
    }
    const { type, value } = action;
    switch (type) {
      case ACTION_ADD_VALUE:
        draft.count += value;
        break;
      case ACTION_SET_TOTAL:
        draft.total = value;
        draft.count = 0;
        break;
      default:
    }
    draft.progress = draft.total ? draft.count / draft.total : 0;
  });
  const [state, dispatch] = useReducer(reducer, { count: 0, total: 0, progress: 0 });
  const filesById = useRef(makeIdObject(files));

  function update() {
    onChange(idValues(filesById.current));
  }

  function handleOne(item) {
    const currentItem = filesById.current[item.id];
    const newProgress = item.progress - (currentItem ? currentItem.progress : 0);
    if (newProgress) {
      dispatch({ type: ACTION_ADD_VALUE, value: newProgress });
    }
    filesById.current[item.id] = item;
    update();
  }

  function handleChange(item) {
    upload(item, handleOne);
  }

  function handleDelete(item) {
    delete filesById.current[item.id];
    filesById.current.list = filesById.current.list.filter((id) => id !== item.id);
    update();
  }

  async function processImage(mediaItem, limitImageCount) {
    let { file } = mediaItem;
    if (!typeTest('image', file)) {
      return mediaItem;
    }
    file = await convertHeic(file);
    if (limitImageCount()) {
      file = await createImageFile(file);
      dispatch({ type: ACTION_ADD_VALUE, value: 100 });
    }
    return { ...mediaItem, file };
  }

  function prepareItem(file) {
    const id = uniqueId('tmp_');
    const mediaItem = {
      file,
      id,
      progress: 0,
      loading: true,
    };
    filesById.current[id] = mediaItem;
    filesById.current.list.push(id);
    return id;
  }
  async function handleAdd(newFiles) {
    const newCounts = getTypeCounts(newFiles);
    const numImages = (newCounts.image || 0);
    dispatch({ type: ACTION_SET_TOTAL, value: Math.min(numImages, MAX_PREVIEWS) + (newFiles.length) });
    if (numImages > MAX_PREVIEWS) {
      setSkipPreviews(true);
    }
    const uploadPromises = [];
    const newIds = newFiles.map(prepareItem);
    let imgCount = 0;
    const limitImageCount = () => imgCount++ < MAX_PREVIEWS;
    for (let i = 0; i < newIds.length; i++) {
      if (!isMounted) {
        return;
      }
      const id = newIds[i];
      const sleepPromise = sleep(MIN_DELAY);
      const mediaItem = await processImage(filesById.current[id], limitImageCount);
      uploadPromises.push(upload(mediaItem, handleOne));
      await sleepPromise;
    }
    update();
    await Promise.all(uploadPromises);
    dispatch({ type: ACTION_SET_TOTAL, value: 0 });
  }

  return { skipPreviews, setSkipPreviews, counts, progress: state.progress, handleChange, handleDelete, handleAdd };
}

export default useAddFiles;
